import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent, CardTitle, CardDescription } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { useAuth } from '../../contexts/AuthContext';
import { useData } from '../../contexts/DataContext';
import { Link, useNavigate } from 'react-router-dom';
import { 
    FileText, Clock, PlayCircle, CheckCircle, X, Send, 
    TrendingUp, Award, Activity, 
    ChevronRight, ArrowUpRight, ArrowDownRight, Info, Play
} from 'lucide-react';
import { 
    LineChart, Line, BarChart, Bar, Cell, 
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
    PieChart, Pie, Legend
} from 'recharts';

export default function PatientDashboard() {
    const { user } = useAuth();
    const { assessments, assignedAssessments } = useData();
    const navigate = useNavigate();

    const [timeFilter, setTimeFilter] = useState('Monthly');

    const myCompletedAssessments = useMemo(() => 
        assessments.filter(a => 
            a.patientId?.toString() === user?.patientId?.toString() || 
            a.patientName === user?.name || 
            a.patientName === user?.patientName
        ), 
    [assessments, user]);

    const myPendingAssessments = useMemo(() => 
        assignedAssessments.filter(a => 
            a.patientId?.toString() === user?.patientId?.toString() || 
            a.patientName === user?.name || 
            a.patientName === user?.patientName
        ),
    [assignedAssessments, user]);

    // Dynamic data based on time filter
    const displayData = useMemo(() => {
        const baseTrend = {
            'Daily': [
                { name: 'Mon', score: 70, baseline: 65 },
                { name: 'Tue', score: 72, baseline: 65 },
                { name: 'Wed', score: 68, baseline: 65 },
                { name: 'Thu', score: 75, baseline: 65 },
                { name: 'Fri', score: 78, baseline: 65 },
                { name: 'Sat', score: 82, baseline: 65 },
                { name: 'Sun', score: 85, baseline: 65 },
            ],
            'Weekly': [
                { name: 'Week 1', score: 65, baseline: 60 },
                { name: 'Week 2', score: 68, baseline: 60 },
                { name: 'Week 3', score: 72, baseline: 60 },
                { name: 'Week 4', score: 75, baseline: 60 },
            ],
            'Monthly': [
                { name: 'Jan', score: 65, baseline: 60 },
                { name: 'Feb', score: 68, baseline: 60 },
                { name: 'Mar', score: 75, baseline: 60 },
                { name: 'Apr', score: 72, baseline: 60 },
                { name: 'May', score: 80, baseline: 60 },
                { name: 'Jun', score: 85, baseline: 60 },
            ],
            'Yearly': [
                { name: '2023', score: 55, baseline: 50 },
                { name: '2024', score: 65, baseline: 50 },
                { name: '2025', score: 78, baseline: 50 },
                { name: '2026', score: 85, baseline: 50 },
            ]
        };

        const baseCategory = {
            'Daily': [
                { category: 'Gross Motor', score: 88 },
                { category: 'Social', score: 65 },
                { category: 'Communication', score: 82 },
            ],
            'Monthly': [
                { category: 'Gross Motor', score: 85 },
                { category: 'Fine Motor', score: 72 },
                { category: 'Communication', score: 68 },
                { category: 'Social Skills', score: 90 },
                { category: 'Vision/Hearing', score: 78 },
            ],
            'Yearly': [
                { category: 'Gross Motor', score: 92 },
                { category: 'Fine Motor', score: 85 },
                { category: 'Communication', score: 70 },
                { category: 'Social Skills', score: 88 },
            ]
        };

        return {
            trend: baseTrend[timeFilter] || baseTrend['Monthly'],
            category: baseCategory[timeFilter] || baseCategory['Monthly'],
            status: [
                { name: 'Mastered', value: timeFilter === 'Yearly' ? 25 : 12, color: '#10b981' },
                { name: 'In Progress', value: timeFilter === 'Yearly' ? 15 : 8, color: '#f59e0b' },
                { name: 'Emerging', value: timeFilter === 'Yearly' ? 10 : 5, color: '#6366f1' },
                { name: 'Not Started', value: timeFilter === 'Yearly' ? 5 : 3, color: '#94a3b8' },
            ],
        };
    }, [timeFilter]);

    // Stats calculations
    const stats = useMemo(() => {
        const total = myCompletedAssessments.length;
        const avg = total > 0 ? (myCompletedAssessments.reduce((sum, a) => sum + (a.percentage || 75), 0) / total).toFixed(1) : 78.4;
        const latest = total > 0 ? myCompletedAssessments[total - 1].percentage || 85 : 85;
        const improvement = total > 1 ? (latest - (myCompletedAssessments[0].percentage || 70)).toFixed(1) : 12.5;
        
        return {
            total,
            avg,
            latest,
            improvement,
            status: improvement >= 0 ? 'Improving' : 'Needs Attention'
        };
    }, [myCompletedAssessments]);

    return (
        <div className="space-y-8 pb-12 font-inter">
            {/* Page Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div>
                    <div className="flex items-center gap-3">
                        <h1 className="text-4xl font-black text-slate-900 font-outfit tracking-tight">Welcome, {user?.name}</h1>
                        <Badge className="bg-slate-50 text-slate-400 border-slate-100 text-[8px] font-bold uppercase tracking-tighter opacity-50">ID: {user?.patientId || 'None'} | Ref: {user?.patientName || 'None'}</Badge>
                    </div>
                    <p className="text-slate-500 mt-2 text-lg font-light">Your clinical progression and developmental markers.</p>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center bg-slate-100 rounded-2xl p-1.5 shadow-inner border border-slate-200">
                        {['Daily', 'Weekly', 'Monthly', 'Yearly'].map((f) => (
                            <button
                                key={f}
                                onClick={() => setTimeFilter(f)}
                                className={`px-6 py-2 text-xs font-black uppercase tracking-widest rounded-xl transition-all ${timeFilter === f ? 'bg-white text-brand-600 shadow-premium' : 'text-slate-400 hover:text-slate-600'}`}
                            >
                                {f}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6">
                <StatCard 
                    label="Assessments" 
                    value={stats.total} 
                    icon={<FileText className="text-brand-600" />} 
                    trend="+2 this month"
                    color="bg-brand-50"
                />
                <StatCard 
                    label="Avg Maturity" 
                    value={`${stats.avg}%`} 
                    icon={<Activity className="text-brand-600" />} 
                    trend="Stable"
                    color="bg-brand-50"
                />
                <StatCard 
                    label="Latest Score" 
                    value={`${stats.latest}%`} 
                    icon={<Award className="text-accent-600" />} 
                    trend="Last week"
                    color="bg-accent-50"
                />
                <StatCard 
                    label="Improvement" 
                    value={`${stats.improvement}%`} 
                    icon={<TrendingUp className={stats.improvement >= 0 ? "text-green-600" : "text-red-500"} />} 
                    isTrend
                    trendValue={stats.improvement}
                    color="bg-green-50"
                />
                <StatCard 
                    label="Growth Status" 
                    value={stats.status} 
                    icon={<CheckCircle className="text-indigo-600" />} 
                    trend="Consistent"
                    color="bg-indigo-50"
                />
            </div>

            {/* Main Analytics Content */}
            <div className="grid lg:grid-cols-3 gap-10">
                {/* Charts Area */}
                <div className="lg:col-span-2 space-y-10">
                    {/* Performance Trend */}
                    <Card className="border-0 shadow-soft overflow-hidden rounded-[2.5rem] bg-white border border-slate-50">
                        <CardHeader className="p-10 pb-0">
                            <div className="flex items-center justify-between">
                                <div>
                                    <CardTitle className="text-2xl font-black text-slate-900 font-outfit tracking-tight uppercase">Performance Velocity</CardTitle>
                                    <CardDescription className="text-slate-400 font-medium">Tracking multi-domain maturity over the {timeFilter.toLowerCase()} period</CardDescription>
                                </div>
                                <div className="h-12 w-12 rounded-2xl bg-slate-50 flex items-center justify-center border border-slate-100 shadow-inner">
                                    <TrendingUp className="h-6 w-6 text-brand-600" />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-10 h-[400px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <LineChart data={displayData.trend}>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" />
                                    <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 800}} dy={10} />
                                    <YAxis axisLine={false} tickLine={false} tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 800}} />
                                    <Tooltip 
                                        contentStyle={{ borderRadius: '24px', border: 'none', boxShadow: '0 25px 50px -12px rgb(0 0 0 / 0.25)', padding: '15px 20px' }}
                                    />
                                    <Line type="monotone" dataKey="score" stroke="#14b8a6" strokeWidth={6} dot={{ r: 8, fill: '#14b8a6', strokeWidth: 4, stroke: '#fff' }} activeDot={{ r: 10 }} />
                                    <Line type="monotone" dataKey="baseline" stroke="#cbd5e1" strokeWidth={2} strokeDasharray="8 8" />
                                </LineChart>
                            </ResponsiveContainer>
                        </CardContent>
                    </Card>

                    <div className="grid md:grid-cols-2 gap-10">
                        {/* Domain Performance */}
                        <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden">
                            <CardHeader className="p-8 pb-0">
                                <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Domain Breakdown</CardTitle>
                            </CardHeader>
                            <CardContent className="p-8 h-[320px]">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={displayData.category} layout="vertical" margin={{ left: 40, right: 40, top: 0, bottom: 0 }}>
                                        <XAxis type="number" hide />
                                        <YAxis 
                                            dataKey="category" 
                                            type="category" 
                                            axisLine={false} 
                                            tickLine={false} 
                                            tick={{fill: '#94a3b8', fontSize: 10, fontWeight: 800}} 
                                            width={80}
                                        />
                                        <Tooltip cursor={{fill: 'transparent'}} />
                                        <Bar dataKey="score" fill="#0d9488" radius={[0, 12, 12, 0]} barSize={24} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>

                        {/* Distribution */}
                        <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 overflow-hidden">
                            <CardHeader className="p-8 pb-0">
                                <CardTitle className="text-xl font-black text-slate-900 font-outfit uppercase tracking-tight">Skill Matrix</CardTitle>
                            </CardHeader>
                            <CardContent className="p-8 h-[320px] flex items-center justify-center">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={displayData.status}
                                            innerRadius={70}
                                            outerRadius={100}
                                            paddingAngle={10}
                                            dataKey="value"
                                        >
                                            {displayData.status.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip contentStyle={{ borderRadius: '20px', border: 'none', boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1)' }} />
                                        <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{fontSize: '10px', fontWeight: '800', textTransform: 'uppercase'}} />
                                    </PieChart>
                                </ResponsiveContainer>
                            </CardContent>
                        </Card>
                    </div>
                </div>

                {/* Sidebar Content */}
                <div className="space-y-10">
                    {/* Growth Insights */}
                    <Card className="border-0 shadow-premium rounded-[2.5rem] bg-slate-900 text-white overflow-hidden relative">
                        <div className="absolute top-0 right-0 w-32 h-32 bg-brand-500/10 rounded-full blur-3xl"></div>
                        <CardHeader className="p-10">
                            <div className="flex items-center gap-4 text-brand-400">
                                <div className="h-12 w-12 rounded-2xl bg-white/10 flex items-center justify-center border border-white/10">
                                    <TrendingUp className="h-6 w-6" />
                                </div>
                                <CardTitle className="text-2xl font-black font-outfit uppercase tracking-tight">Growth Data</CardTitle>
                            </div>
                        </CardHeader>
                        <CardContent className="px-10 pb-10 space-y-8">
                            <InsightItem 
                                color="bg-green-400" 
                                text={`You improved by ${timeFilter === 'Yearly' ? '24%' : '10%'} in wellness markers over this period.`} 
                            />
                            <InsightItem 
                                color="bg-brand-400" 
                                text="Consistent progress observed in communication and motor skills categories." 
                            />
                            
                            <div className="pt-8 border-t border-white/10 mt-6">
                                <Button className="w-full h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-lg transition-all" onClick={() => navigate('/patient/analysis')}>
                                    Full Analysis Report <ChevronRight className="ml-3 h-4 w-4" />
                                </Button>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Pending Assignments */}
                    <Card className="border-0 shadow-soft rounded-[2.5rem] bg-white border border-slate-50 border-t-[8px] border-t-brand-600 overflow-hidden">
                        <CardHeader className="p-8">
                            <div className="flex items-center justify-between mb-6">
                                <Badge className="bg-amber-50 text-amber-600 border-amber-100 px-4 py-1.5 uppercase tracking-[0.2em] text-[10px] font-black rounded-xl">Portal Action</Badge>
                            </div>
                            <CardTitle className="text-2xl font-black text-slate-900 font-outfit uppercase tracking-tight">Assignments</CardTitle>
                            <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-1">
                                Available: {myPendingAssessments.length}
                            </p>
                        </CardHeader>
                        <CardContent className="px-8 pb-8 pt-0 text-center">
                            {myPendingAssessments.length > 0 ? (
                                <div className="space-y-6">
                                    {myPendingAssessments.map((assignment, index) => (
                                        <div key={index} className="space-y-4">
                                            <div className="p-6 bg-slate-50 rounded-[2rem] border border-slate-100 shadow-inner relative overflow-hidden">
                                                <div className="absolute top-0 right-0">
                                                    <Badge className="bg-brand-600 text-white border-0 rounded-bl-xl rounded-tr-none px-3 py-1 text-[9px] font-black uppercase tracking-widest">
                                                        {assignment.frequency || 'Priority'}
                                                    </Badge>
                                                </div>
                                                <p className="font-black text-slate-900 font-outfit uppercase text-lg mb-3 mt-2">{assignment.assessmentTitle}</p>
                                                <div className="flex items-center justify-center gap-6 text-[10px] font-black uppercase tracking-widest text-slate-400">
                                                    <span className="flex items-center"><Clock className="mr-2 h-4 w-4" /> {assignment.duration}</span>
                                                    <span className="flex items-center"><Activity className="mr-2 h-4 w-4" /> Targeted</span>
                                                </div>
                                            </div>
                                            <Button className="w-full h-14 bg-brand-600 hover:bg-brand-700 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-premium transition-all" onClick={() => navigate('/patient/assessment', { state: { requiredType: assignment.assessmentTitle } })}>
                                                Initialize Form <PlayCircle className="ml-3 h-5 w-5" />
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="py-12">
                                    <div className="h-20 w-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 border border-slate-100">
                                        <CheckCircle className="h-10 w-10 text-slate-200" />
                                    </div>
                                    <p className="text-slate-400 font-bold uppercase tracking-widest text-xs">All modules normalized</p>
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </div>
            </div>
        </div>
    );
}

function StatCard({ label, value, icon, trend, color, isTrend, trendValue }) {
    return (
        <Card className="border-0 shadow-soft p-8 flex flex-col justify-between overflow-hidden relative rounded-[2.5rem] hover:shadow-premium hover:-translate-y-1 transition-all group bg-white border border-slate-50">
            <div className={`absolute -right-6 -top-6 w-24 h-24 rounded-full ${color} opacity-10 blur-xl group-hover:opacity-20 transition-opacity`}></div>
            <div className="relative z-10">
                <div className="flex items-center justify-between mb-6">
                    <div className={`h-12 w-12 rounded-2xl ${color} flex items-center justify-center shadow-sm border border-white/50 group-hover:scale-110 transition-transform`}>
                        {icon}
                    </div>
                </div>
                <h4 className="text-slate-400 text-[10px] font-black uppercase tracking-[0.2em] mb-2">{label}</h4>
                <p className="text-3xl font-black text-slate-900 mb-2 font-outfit">{value}</p>
                <div className="flex items-center gap-2">
                    {isTrend ? (
                        <div className={`flex items-center text-[10px] font-black px-2 py-0.5 rounded-lg ${trendValue >= 0 ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-500'}`}>
                            {trendValue >= 0 ? <ArrowUpRight className="h-3 w-3 mr-1" /> : <ArrowDownRight className="h-3 w-3 mr-1" />}
                            {Math.abs(trendValue)}%
                        </div>
                    ) : (
                        <span className="text-[10px] font-black text-slate-400 uppercase tracking-tighter">{trend}</span>
                    )}
                </div>
            </div>
        </Card>
    );
}

function InsightItem({ color, text }) {
    return (
        <div className="flex gap-5 items-start bg-white/5 p-5 rounded-[2rem] border border-white/5 hover:bg-white/10 transition-colors">
            <div className={`mt-2 h-2.5 w-2.5 rounded-full ${color} shadow-lg shrink-0`}></div>
            <p className="text-sm text-slate-300 leading-relaxed font-light">{text}</p>
        </div>
    );
}
